import { createContext, useContext, useEffect, useState, useCallback, use } from 'react';
import type { ReactNode } from 'react';
import { LazyDataLoader } from '@/lib/utils/lazy-data-loader';
import { DBCollection } from '@/lib/utils/constants-shared';

interface PeopleByPassageDataContextType {
  peopleByPassage: PeopleByPassage[];
  getPeopleByPassageByPassageId: (passageId: string) => PeopleByPassage | undefined;
  loadPeopleByPassageByPassageId: (passageId: string) => Promise<PeopleByPassage | undefined>;
}

export const PeopleByPassageDataContext = createContext<PeopleByPassageDataContextType | undefined>(undefined);

export function usePeopleByPassageData(passageId?: string) {
  const context = use(PeopleByPassageDataContext);
  const [data, setData] = useState<PeopleByPassage | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!passageId || !context) {
      // If no passageId, return the context methods for backward compatibility
      return;
    }

    const cached = context.getPeopleByPassageByPassageId(passageId);
    if (cached) {
      setData(cached);
      return;
    }

    setIsLoading(true);
    context.loadPeopleByPassageByPassageId(passageId)
      .then(loaded => {
        setData(loaded);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(`Failed to load people by passage for ${passageId}:`, err);
        setIsLoading(false);
      });
  }, [passageId, context]);

  // Return both the loaded data and the context methods for backward compatibility
  return {
    data,
    isLoading,
    ...context
  };
}

interface PeopleByPassageDataProviderProps {
  children: ReactNode;
  peopleByPassage: PeopleByPassage[];
}

export function PeopleByPassageDataProvider({ children, peopleByPassage: initialData }: PeopleByPassageDataProviderProps) {
  const [loader] = useState(() => new LazyDataLoader<PeopleByPassage>(DBCollection.peopleByPassage, 'passageId'));
  const [cachedItems, setCachedItems] = useState<PeopleByPassage[]>([]);

  useEffect(() => {
    if (initialData && initialData.length > 0) {
      loader.preload(initialData);
      setCachedItems(loader.getAllCached());
    }
  }, [initialData, loader]);

  const getPeopleByPassageByPassageId = useCallback((passageId: string): PeopleByPassage | undefined => {
    return cachedItems.find(item => item.passageId === passageId);
  }, [cachedItems]);

  const loadPeopleByPassageByPassageId = useCallback(async (passageId: string): Promise<PeopleByPassage | undefined> => {
    const item = await loader.get(passageId);
    if (item) {
      setCachedItems(loader.getAllCached());
    }
    return item;
  }, [loader]);

  return (
    <PeopleByPassageDataContext.Provider
      value={{
        peopleByPassage: cachedItems,
        getPeopleByPassageByPassageId,
        loadPeopleByPassageByPassageId
      }}
    >
      {children}
    </PeopleByPassageDataContext.Provider>
  );
}

